import { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setProjectId, clearProject } from '../store/slices/projectSlice';
import { projectService } from '../services/projectService';

/**
 * Hook that handles project route guards and URL syncing
 * - Syncs Redux projectId with URL params
 * - Validates project access and existence
 * - Redirects to appropriate routes when needed
 */
export const useProjectRouteGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const storeProjectId = useSelector((state: RootState) => state.project.projectId);

  useEffect(() => {
    const handleProjectRouteGuard = async () => {
      const isProjectRoute = location.pathname.startsWith('/projects/');
      const isDashboardRoute = location.pathname === '/dashboard';

      // Case 1: On project route with URL projectId
      if (isProjectRoute && urlProjectId) {
        // If URL projectId differs from store, sync the store
        if (urlProjectId !== storeProjectId) {
          try {
            // Validate project exists and user has access
            const response = await projectService.getProject(urlProjectId);

            if (response.success && response.data) {
              // Valid project - sync to store
              dispatch(setProjectId(urlProjectId));
            } else {
              // Invalid project - clear and redirect
              dispatch(clearProject());
              navigate('/dashboard', { replace: true });
            }
          } catch (error) {
            console.error('Failed to validate project access:', error);
            // Network error or no access - clear and redirect
            dispatch(clearProject());
            navigate('/dashboard', { replace: true });
          }
        }
      }

      // Case 2: On project route but no URL projectId (invalid route)
      else if (isProjectRoute && !urlProjectId) {
        // Invalid project route - redirect to dashboard
        dispatch(clearProject());
        navigate('/dashboard', { replace: true });
      }

      // Case 3: On dashboard route but have store projectId (user manually navigated)
      else if (isDashboardRoute && storeProjectId) {
        // Keep store projectId for potential project selector usage
        // Don't auto-redirect - let user choose
      }

      // Case 4: Non-project route and have store projectId
      else if (!isProjectRoute && !isDashboardRoute && storeProjectId) {
        // On other routes like settings, keep project context
        // Don't interfere with navigation
      }
    };

    handleProjectRouteGuard();
  }, [location.pathname, urlProjectId, storeProjectId, dispatch, navigate]);

  /**
   * Navigate to a project module while maintaining project context
   */
  const navigateToProjectModule = (projectId: string, module: string = 'dashboard') => {
    dispatch(setProjectId(projectId));
    navigate(`/projects/${projectId}/${module}`);
  };

  /**
   * Switch projects while maintaining current module
   */
  const switchProject = (newProjectId: string) => {
    const currentPath = location.pathname;
    const isOnProjectRoute = currentPath.startsWith('/projects/');

    if (isOnProjectRoute) {
      // Extract current module from path
      const pathParts = currentPath.split('/');
      const currentModule = pathParts[3] || 'dashboard';

      // Navigate to same module on new project
      navigateToProjectModule(newProjectId, currentModule);
    } else {
      // Not on project route - go to project dashboard
      navigateToProjectModule(newProjectId, 'dashboard');
    }
  };

  /**
   * Clear project selection and go to org dashboard
   */
  const clearProjectAndGoToDashboard = () => {
    dispatch(clearProject());
    navigate('/dashboard');
  };

  return {
    currentProjectId: storeProjectId,
    urlProjectId,
    isOnProjectRoute: location.pathname.startsWith('/projects/'),
    navigateToProjectModule,
    switchProject,
    clearProjectAndGoToDashboard,
  };
};
